import { useQuery } from '@apollo/client/react'
import { groupBy } from 'lodash'
import { useEffect } from 'react'
import type {
  RequestedDocumentsQuery,
  RequestedReturnsQuery,
  RequestedReturnsQueryVariables,
} from '../generated/graphql'
import {
  RequestedDocumentsDocument,
  RequestedReturnsDocument,
} from '../generated/graphql'

type Return = Extract<
  RequestedReturnsQuery['containersConnection']['edges'][0]['node'],
  { __typename: 'ContainerReturn' }
>
export type Document = Extract<
  RequestedDocumentsQuery['containersConnection']['edges'][0]['node'],
  { __typename: 'ContainerDocument' }
>
export type PaginatedReturn = Return & {
  documents: Document[]
  finishedBy: string
}
export type PaginatedReturnsQueryResult = useQuery.Result<
  RequestedReturnsQuery,
  RequestedReturnsQueryVariables
> & {
  loading: boolean
  total: number
  next?: () => void
  returns: PaginatedReturn[]
}

const usePaginatedDocuments = (ids: string[], count = 1000) => {
  const returnsQuery = useQuery(RequestedDocumentsDocument, {
    variables: {
      count,
      returnIds: ids,
    },
    skip: ids.length < 1,
  })
  const containersConnection =
    returnsQuery.data && returnsQuery.data.containersConnection
  if (!containersConnection) {
    return {
      ...returnsQuery,
      total: 0,
      next: undefined,
      documents: [],
    }
  }
  return {
    ...returnsQuery,
    total: containersConnection.aggregate.count,
    documents: containersConnection.edges
      .map(edge => edge.node)
      .filter(
        (node): node is Document => node.__typename === 'ContainerDocument'
      ),
    next: containersConnection.pageInfo.hasNextPage
      ? () => {
          const containersConnection =
            returnsQuery.data && returnsQuery.data.containersConnection
          return returnsQuery.fetchMore({
            variables: {
              ...returnsQuery.variables,
              cursor:
                containersConnection && containersConnection.pageInfo.endCursor,
            },
            updateQuery(previousResult, { fetchMoreResult }) {
              if (!fetchMoreResult) {
                return previousResult
              }
              const {
                edges = [],
                pageInfo,
                aggregate,
              } = fetchMoreResult.containersConnection
              return {
                ...previousResult,
                containersConnection: {
                  __typename: previousResult.containersConnection.__typename,
                  edges: [
                    ...previousResult.containersConnection.edges,
                    ...edges,
                  ],
                  pageInfo,
                  aggregate,
                },
              }
            },
          })
        }
      : undefined,
  }
}

export const usePaginatedReturns = (
  where: RequestedReturnsQueryVariables['where'],
  count = 1000
): PaginatedReturnsQueryResult => {
  const returnsQuery = useQuery(RequestedReturnsDocument, {
    variables: {
      count,
      where,
    },
  })
  const containersConnection =
    returnsQuery.data && returnsQuery.data.containersConnection
  const returns = containersConnection
    ? containersConnection.edges
        .map(edge => edge.node)
        .filter((node): node is Return => node.__typename === 'ContainerReturn')
    : []
  const documentsQuery = usePaginatedDocuments(returns.map(item => item.id))

  useEffect(() => {
    if (!documentsQuery.loading && documentsQuery.next) {
      documentsQuery.next()
    }
  }, [documentsQuery])

  if (!containersConnection) {
    return {
      ...returnsQuery,
      total: 0,
      next: undefined,
      returns: [],
    }
  }
  const documentsByReturnId = groupBy(
    documentsQuery.documents,
    item => item.returnId
  )

  return {
    ...returnsQuery,
    loading: returnsQuery.loading || documentsQuery.loading,
    total: containersConnection.aggregate.count,
    returns: returns.map(item => ({
      ...item,
      finishedBy: item.type === 'ReturnImmediate' ? 'user' : 'month',
      documents: documentsByReturnId[item.id] || [],
    })),
    next: containersConnection.pageInfo.hasNextPage
      ? () => {
          const containersConnection =
            returnsQuery.data && returnsQuery.data.containersConnection
          return returnsQuery.fetchMore({
            variables: {
              ...returnsQuery.variables,
              cursor:
                containersConnection && containersConnection.pageInfo.endCursor,
            },
            updateQuery(previousResult, { fetchMoreResult }) {
              if (!fetchMoreResult) {
                return previousResult
              }
              const {
                edges = [],
                pageInfo,
                aggregate,
              } = fetchMoreResult.containersConnection
              return {
                ...previousResult,
                containersConnection: {
                  __typename: previousResult.containersConnection.__typename,
                  edges: [
                    ...previousResult.containersConnection.edges,
                    ...edges,
                  ],
                  pageInfo,
                  aggregate,
                },
              }
            },
          })
        }
      : undefined,
  }
}
